/**
 * Async establishment search with multi-select and label cache.
 * Defaults `operationalOnly=true` so create forms only offer validated establishments.
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { searchEstablishments } from '@/services/establishments.service';
import type { SelectOption } from '@/components/forms/Select';
import { formatEstablishmentSelectLabel } from '@/utils/format-establishment-select-label-utils';
import type { Establishment } from '@/types/establishment.types';
/** Parse `"Jules (34)"` or `"Jules (Seine)"` → `{ nameQuery, department }`. */
function parseEstablishmentQuery(raw: string): { nameQuery: string; department?: string } {
  const trimmed = raw.trimEnd();
  if (!trimmed.endsWith(')')) return { nameQuery: raw };
  const open = trimmed.lastIndexOf('(');
  if (open <= 0) return { nameQuery: raw };
  const department = trimmed.slice(open + 1, -1).trim();
  if (!department) return { nameQuery: raw };
  return { nameQuery: trimmed.slice(0, open).trim(), department };
}

/** True when an establishment matches the local name / department search query. */
function matchesLocalEstablishmentSearch(
  establishment: Pick<Establishment, 'name'> &
    Partial<Pick<Establishment, 'city' | 'department' | 'postalCode' | 'country' | 'uaiCode'>>,
  rawQuery: string,
): boolean {
  const trimmed = rawQuery.trim();
  if (!trimmed) return true;
  const { nameQuery, department } = parseEstablishmentQuery(trimmed);
  const label = formatEstablishmentSelectLabel(establishment).toLowerCase();
  const name = establishment.name?.toLowerCase() ?? '';
  const uai = establishment.uaiCode?.toLowerCase() ?? '';
  const nameOk =
    nameQuery.length === 0 ||
    name.includes(nameQuery.toLowerCase()) ||
    label.includes(nameQuery.toLowerCase()) ||
    uai.includes(nameQuery.toLowerCase());
  if (!nameOk) return false;
  if (!department) return true;
  const dept = department.toLowerCase();
  const estDept = establishment.department?.toLowerCase() ?? '';
  const postalPrefix = establishment.postalCode?.slice(0, department.length) ?? '';
  return estDept.includes(dept) || postalPrefix === department || label.includes(`(${dept}`);
}

/** Async establishment search with multi-select and a label cache (default `operationalOnly`). */
export const useEstablishmentsMultiselect = (hookOptions?: {
  operationalOnly?: boolean;
  /** When set, search is limited to these establishments (client-side filter). */
  restrictToEstablishments?: Array<
    Pick<Establishment, 'name' | 'id'> &
      Partial<Pick<Establishment, 'city' | 'department' | 'postalCode' | 'country' | 'uaiCode'>>
  >;
}) => {
  const operationalOnly = hookOptions?.operationalOnly ?? true;
  const restrictToEstablishments = hookOptions?.restrictToEstablishments;
  const isLocallyRestricted = restrictToEstablishments !== undefined;
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState<any[]>([]);
  const selectedLabelsCache = useRef<Map<string, string>>(new Map());

  const { nameQuery, department } = parseEstablishmentQuery(searchQuery);

  const { data, isLoading } = useQuery({
    queryKey: ['establishments-multiselect-search', nameQuery, department, operationalOnly, page],
    queryFn: () => searchEstablishments(nameQuery, page, 10, department, { operationalOnly }),
    enabled:
      !isLocallyRestricted && (nameQuery.length >= 2 || (!!department && nameQuery.length >= 1)),
  });

  /** Accumulate pages as they load (remote search only). */
  useMemo(() => {
    if (isLocallyRestricted || !data?.data) return;

    if (page === 1) {
      setAccumulatedData(data.data);
    } else {
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((e: any) => e.id));
        const uniqueNew = data.data.filter((e: any) => !existingIds.has(e.id));
        return [...prev, ...uniqueNew];
      });
    }
  }, [data, page, isLocallyRestricted]);

  const localSearchOptions: SelectOption[] = useMemo(() => {
    if (!isLocallyRestricted || !restrictToEstablishments) return [];
    const pool = restrictToEstablishments.length > 0 ? restrictToEstablishments : [];
    return pool
      .filter((establishment) => matchesLocalEstablishmentSearch(establishment, searchQuery))
      .map((establishment) => {
        const label = formatEstablishmentSelectLabel(establishment);
        selectedLabelsCache.current.set(establishment.id, label);
        return {
          value: establishment.id,
          label,
        };
      });
  }, [isLocallyRestricted, restrictToEstablishments, searchQuery]);

  useEffect(() => {
    if (!isLocallyRestricted || !restrictToEstablishments) return;
    for (const establishment of restrictToEstablishments) {
      selectedLabelsCache.current.set(
        establishment.id,
        formatEstablishmentSelectLabel(establishment),
      );
    }
  }, [isLocallyRestricted, restrictToEstablishments]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
    setAccumulatedData([]);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isLocallyRestricted && data?.hasMore) {
      setPage((p) => p + 1);
    }
  }, [isLocallyRestricted, data]);

  /** Pre-fill labels for known establishment ids (e.g. from GET student) so {@link MultiSelect} shows tags and options before the user types a search. */
  const primeLabelCache = useCallback(
    (
      entries: Array<
        Pick<Establishment, 'name'> & { id: string } & Partial<
            Pick<Establishment, 'city' | 'department' | 'postalCode' | 'country'>
          >
      >,
    ) => {
      for (const entry of entries) {
        const name = entry.name?.trim();
        if (!entry.id || !name) continue;
        selectedLabelsCache.current.set(
          entry.id,
          formatEstablishmentSelectLabel({ ...entry, name }),
        );
      }
    },
    [],
  );

  const remoteSearchOptions: SelectOption[] = (isLocallyRestricted ? [] : accumulatedData).map(
    (establishment) => {
      const label = formatEstablishmentSelectLabel(establishment);
      selectedLabelsCache.current.set(establishment.id, label);
      return {
        value: establishment.id,
        label,
      };
    },
  );

  const searchOptions = isLocallyRestricted ? localSearchOptions : remoteSearchOptions;
  /**
   * Build the options for currently selected values whose labels are cached
   * but not in current search results (keeps tags visible after search clears).
   */
  const getOptionsForValues = useCallback(
    (selectedIds: string[]): SelectOption[] => {
      const searchResultIds = new Set(searchOptions.map((o) => o.value));
      const extraOptions: SelectOption[] = selectedIds
        .filter((id) => !searchResultIds.has(id) && selectedLabelsCache.current.has(id))
        .map((id) => ({
          value: id,
          label: selectedLabelsCache.current.get(id) ?? id,
        }));
      return [...extraOptions, ...searchOptions];
    },
    [searchOptions],
  );

  /** Pre-fill the label cache (e.g. establishments already selected on load). */
  const preloadLabels = useCallback((entries: { id: string; label: string }[]) => {
    entries.forEach(({ id, label }) => {
      selectedLabelsCache.current.set(id, label);
    });
  }, []);

  return {
    searchOptions,
    isLoading: isLocallyRestricted ? false : isLoading,
    onSearch: handleSearch,
    onLoadMore: handleLoadMore,
    hasMore: isLocallyRestricted ? false : data?.hasMore || false,
    getOptionsForValues,
    preloadLabels,
    primeLabelCache,
    searchQuery,
    isSearchLoading: isLoading,
  };
};
